import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AsicStatus } from "@models/documents/asic-status.enum";
import { Document } from "@models/documents/document";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import { NotificationComponent } from "@shared/components/commons/notification/notification.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { ReplaceStrPipe } from "@shared/pipes/replace-str.pipe";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { ToastrService } from "ngx-toastr";
import { catchError, of, Subject } from "rxjs";
import { CompanyMistakeCorrection, CompanyWithdrawLodgedDocumentChange } from "../../../forms/models/company";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Component({
  selector: 'app-asic-response',
  standalone: true,
  imports: [
    NotificationComponent,
    ButtonComponent,
    ReplaceStrPipe
  ],
  providers: [],
  templateUrl: './asic-response.component.html',
  styleUrl: './asic-response.component.scss'
})
export class AsicResponseComponent implements OnInit {
  private documentsService = inject(DocumentsService);
  private documentsApiService = inject(DocumentsApiService);
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  @Input() document!: Document;
  @Input() confirmAction$: Subject<boolean> | undefined;

  @Output() nextStep = new EventEmitter<boolean>();
  @Output() editDocument = new EventEmitter<boolean>();

  protected readonly AsicStatus = AsicStatus;
  readonly successMsg = 'The form has been successfully lodged on. ASIC document number for your reference is:'
  readonly markedAsLodgedMsg = 'The document was manually marked as lodged. ASIC document number for your reference is:'
  readonly rejectedMsg = 'ASIC rejected the lodgement due to this reason:';
  readonly notLodgedFormMsg = 'The form you have prepared cannot be lodged electronically. Please ensure that' +
    ' you print the form and submit it manually to ASIC. It is your responsibility to review the form for accuracy ' +
    'before lodging, as any errors may result in processing delays or additional fees.\n' +
    'For details on how to manually lodge this form, including the correct submission method and any applicable fees,' +
    ' please refer to the ASIC website or contact ASIC directly for guidance.';
  readonly notBeLodgedFormTypeList = ['c:ra01', 'c:ra04', 'c:106', 'c:211', 'c:280', 'c:281', 'c:2205', 'Dividend Statement'];
  notLodgedForm = false;

  constructor() {
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  ngOnInit(): void {
    this.documentsService.disabledHeaderBtn.set(true);
    this.notLodgedForm = this.notBeLodgedFormTypeList.includes(this.document.type);
  }

  requestCorrection(): void {
    const company = this.document.company;
    this.modalFormsService.openModalWithCompany(new CompanyMistakeCorrection(), company, false, {},{});
  }

  withdraw(): void {
    const company = this.document.company;
    this.modalFormsService.openModalWithCompany(new CompanyWithdrawLodgedDocumentChange(), company, false, {},{});
  }

  editInitialDocument(): void {
    this.editDocument.emit(true);
  }

  deleteDocument(): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    (modalRef.componentInstance as ConfirmModalComponent).title = 'Delete document';
    (modalRef.componentInstance as ConfirmModalComponent).message = `Are you sure you want to delete document?`;
    (modalRef.componentInstance as ConfirmModalComponent).confirmText = 'Delete';
    (modalRef.componentInstance as ConfirmModalComponent).confirm = () => this.documentsApiService.deleteDocument(this.document.documentId).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Error while deleting document", "Error");
        return of('ERR');
      })
    );

    modalRef.closed.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res) => {
      if(res !== 'ERR') {
        this.toastr.success('Document deleted!', 'Success');
        void this.router.navigate(['/documents']);
      }
    });
  }
}
